/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2022.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {AutocompleteRenderGroupParams, Button, Checkbox, Chip, CircularProgress, FilterOptionsState, Icon, Paper} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import {AdornmentType} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/AdornmentType";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QPossibleValue} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QPossibleValue";
import {QPossibleValueSourceSection} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QPossibleValueSourceSection";
import {QPossibleValueSourceTable} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QPossibleValueSourceTable";
import {QQueryFilter} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QQueryFilter";
import {ErrorMessage, useFormikContext} from "formik";
import colors from "qqq/assets/theme/base/colors";
import {buildSeedValuesFromFilter} from "qqq/components/forms/possibleValueSourceActions";
import MDTypography from "qqq/components/legacy/MDTypography";
import RecordPickerModal from "qqq/components/misc/RecordPickerModal";
import {FieldPossibleValueProps} from "qqq/models/fields/FieldPossibleValueProps";
import RecordScreenModal from "qqq/pages/records/RecordScreenModal";
import Client from "qqq/utils/qqq/Client";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";

interface Props
{
   fieldPossibleValueProps: FieldPossibleValueProps;
   overrideId?: string;
   name?: string;
   fieldLabel: string;
   inForm: boolean;
   initialValue?: any;
   initialValues?: QPossibleValue[];
   onChange?: any;
   isEditable?: boolean;
   isMultiple?: boolean;
   limitTags?: number;
   bulkEditMode?: boolean;
   bulkEditSwitchChangeHandler?: any;
   otherValues?: Map<string, any>;
   variant: "standard" | "outlined";
   initiallyOpen: boolean;
   openOnFocus?: boolean;
   useCase: "form" | "filter";
   processUUID?: string | null;
}

DynamicSelect.defaultProps = {
   inForm: true,
   initialValue: null,
   initialValues: undefined,
   onChange: null,
   isEditable: true,
   isMultiple: false,
   limitTags: 5,
   bulkEditMode: false,
   otherValues: new Map<string, any>(),
   variant: "outlined",
   initiallyOpen: false,
   bulkEditSwitchChangeHandler: () =>
   {
   },
};

const {inputBorderColor, grayLines} = colors;

////////////////////////////////////////////////////////////////////////////////////////
// how many records one trip through the Search picker may add to a multi-select.     //
//                                                                                     //
// The picker is asked for this many PLUS ONE, which is how an over-large selection is //
// told apart from one that just fits (RecordGridWidget's bulk-add does the same).     //
//                                                                                     //
// Not a larger number, because the ids go back out as a `?ids=a,b,c` QUERY STRING to  //
// resolve their labels: with string primary keys, a few hundred already approaches    //
// the 8KB request-header limit servers commonly default to, and that failure arrives  //
// as an opaque 431 rather than as anything a user could act on.  A multi-select field //
// stores its ids comma-delimited in one value, so this is well past its useful range  //
// in any case.                                                                        //
////////////////////////////////////////////////////////////////////////////////////////
const MULTI_SELECT_PICK_MAX_RECORDS = 200;

///////////////////////////////////////////////////////////////////////////////////////
// the source-table action buttons pinned under the option list.  Buttons, and not   //
// MenuItems: as full-width rows carrying the same hover shading as an option, they  //
// read as two more things to pick from - and the boundary between the list and the  //
// actions vanished exactly where it needed to be clearest, against a highlighted    //
// option directly above.  A different shape settles that structurally rather than   //
// by tuning a hover color to be distinct enough.                                    //
//                                                                                   //
// A plain text Button also arrives in the right color: the app's other "Add new"    //
// (Widget's AddNewRecordButton) is one, and .qqq-themed .MuiButton-textPrimary maps //
// it to the primary color - so the actions stop being the only unaccented text in   //
// a popup whose options are all accented.                                           //
//                                                                                   //
// The minHeight is the point of the sx: the theme floors a text-variant button at   //
// 40px, which is taller than the two MenuItem rows this replaces were EACH, and     //
// would spend the space this change is meant to save.                               //
///////////////////////////////////////////////////////////////////////////////////////
const sourceTableActionButtonSX = {
   minHeight: "32px",
   padding: "0.25rem 0.5rem",

   /////////////////////////////////////////////////////////////////////////////////////
   // "Add new" is two words, and a dropdown is only as wide as the field it belongs  //
   // to - which for a field in a grid cell is narrow enough to break it across two   //
   // lines.  The row wraps instead (see the container), so a label that does not fit //
   // beside its neighbor gets a line of its own rather than being folded in half.    //
   /////////////////////////////////////////////////////////////////////////////////////
   whiteSpace: "nowrap",
};


export const getAutocompleteOutlinedStyle = (isDisabled: boolean) =>
{
   return ({
      "& .MuiOutlinedInput-root": {
         borderRadius: "0.75rem",
      },
      "& .MuiInputBase-root": {
         padding: "0.5rem",
         background: isDisabled ? "#f0f2f5!important" : "initial",
      },
      "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
         padding: "0",
         fontSize: "1rem"
      },
      "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
         borderColor: inputBorderColor
      },
      ".compactForm &": {
         margin: 0,
      },
      ".compactForm & .MuiInputBase-root": {
         padding: "0 6px",
      },
      ".compactForm & .MuiOutlinedInput-root": {
         borderRadius: "3px",
         minHeight: "26px",
      },
      ".compactForm & .MuiOutlinedInput-root .MuiAutocomplete-input": {
         fontSize: "0.875rem",
      },
      ".compactForm & .MuiAutocomplete-tag": {
         padding: "0 0.25rem",
      },
      ".compactForm & .MuiFormControl-root": {
         margin: 0,
      },
   });
};


const qController = Client.getInstance();


function DynamicSelect({fieldPossibleValueProps, overrideId, name, fieldLabel, inForm, initialValue, initialValues, onChange, isEditable, isMultiple, limitTags, bulkEditMode, bulkEditSwitchChangeHandler, otherValues, variant, initiallyOpen, openOnFocus, useCase, processUUID}: Props)
{
   const {fieldName, initialDisplayValue, possibleValueSourceName, possibleValues, processName, tableName} = fieldPossibleValueProps;

   const [open, setOpen] = useState(initiallyOpen);
   const [options, setOptions] = useState<readonly QPossibleValue[]>([]);
   const [sections, setSections] = useState<QPossibleValueSourceSection[]>([]);
   const [sourceTable, setSourceTable] = useState(null as QPossibleValueSourceTable);
   const [addNewModalIsOpen, setAddNewModalIsOpen] = useState(false);
   const [searchModalIsOpen, setSearchModalIsOpen] = useState(false);
   const [sourceTableMessage, setSourceTableMessage] = useState(null as string);

   ////////////////////////////////////////////////////////////////////////////////////
   // when a modal supplies a value, we remount the Autocomplete with a new          //
   // defaultValue rather than converting it to a controlled component - this        //
   // component has many call sites relying on its uncontrolled behavior.  undefined //
   // here means "no modal has supplied a value", which is distinct from null.       //
   ////////////////////////////////////////////////////////////////////////////////////
   const [valueFromModal, setValueFromModal] = useState(undefined as any);
   const [remountKey, setRemountKey] = useState(0);

   ////////////////////////////////////////////////////////////////////////////////////
   // the multi-select's live value, tracked on EVERY change - not just modal-driven //
   // ones.  Neither defaultValue (captured at mount) nor valueFromModal (written    //
   // only by the modal flows) knows about options the user picked straight from the //
   // dropdown list, so a modal-driven add built off either of those silently drops  //
   // them.  A ref rather than state, since it survives the Autocomplete's remount   //
   // and nothing renders from it.                                                   //
   ////////////////////////////////////////////////////////////////////////////////////
   const currentMultipleValues = useRef<any[]>(initialValues ?? []);
   const [searchTerm, setSearchTerm] = useState(null);
   const [firstRender, setFirstRender] = useState(true);
   const [otherValuesWhenResultsWereLoaded, setOtherValuesWhenResultsWereLoaded] = useState(JSON.stringify(Object.fromEntries((otherValues))));

   useEffect(() =>
   {
      if (tableName && processName)
      {
         console.log("DynamicSelect - you may not provide both a tableName and a processName");
      }
      if (tableName && !fieldName)
      {
         console.log("DynamicSelect - if you provide a tableName, you must also provide a fieldName");
      }
      if (processName && !fieldName)
      {
         console.log("DynamicSelect - if you provide a processName, you must also provide a fieldName");
      }
      if (!fieldName && !possibleValueSourceName)
      {
         console.log("DynamicSelect - you must provide either a fieldName (and a tableName or processName) or a possibleValueSourceName");
      }
      if (fieldName && !possibleValueSourceName)
      {
         if (!tableName || !processName)
         {
            console.log("DynamicSelect - if you provide a fieldName and not a possibleValueSourceName, then you must also provide a tableName or processName");
         }
      }
      if (possibleValueSourceName)
      {
         if (tableName || processName)
         {
            console.log("DynamicSelect - if you provide a possibleValueSourceName, you should not also provide a tableName or processName");
         }
      }

   }, [tableName, processName, fieldName, possibleValueSourceName]);

   ////////////////////////////////////////////////////////////////////////////////////////////////
   // default value - needs to be an array (from initialValues (array) prop) for multiple mode - //
   // else non-multiple, assume we took in an initialValue (id) and initialDisplayValue (label), //
   // and build a little object that looks like a possibleValue out of those                     //
   ////////////////////////////////////////////////////////////////////////////////////////////////
   let [defaultValue, _] = isMultiple ? useState(initialValues ?? undefined)
      : useState(initialValue && initialDisplayValue ? [{id: initialValue, label: initialDisplayValue}] : null);

   if (isMultiple && defaultValue === null)
   {
      defaultValue = [];
   }

   // const loading = open && options.length === 0;
   const [loading, setLoading] = useState(false);
   const [switchChecked, setSwitchChecked] = useState(false);
   const [isDisabled, setIsDisabled] = useState(!isEditable || bulkEditMode);
   const [tableMetaData, setTableMetaData] = useState(null as QTableMetaData);

   let setFieldValueRef: (field: string, value: any, shouldValidate?: boolean) => void = null;
   if (inForm)
   {
      const {setFieldValue} = useFormikContext();
      setFieldValueRef = setFieldValue;
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   const filterInlinePossibleValues = (searchTerm: string, possibleValues: QPossibleValue[]): QPossibleValue[] =>
   {
      ///////////////////////////////////////////////////////////////////////////////////////////////////////////
      // sometimes search term is null - which we'd like to treat the same as empty (e.g., return all results) //
      ///////////////////////////////////////////////////////////////////////////////////////////////////////////
      if(searchTerm == null || searchTerm == undefined)
      {
         searchTerm = "";
      }

      return possibleValues.filter(pv => pv.label?.toLowerCase().startsWith(searchTerm.toLowerCase()));
   };


   /***************************************************************************
    **
    ***************************************************************************/
   const loadResults = async (): Promise<{results: QPossibleValue[]; sections: QPossibleValueSourceSection[]; sourceTable: QPossibleValueSourceTable}> =>
   {
      if (possibleValues)
      {
         /////////////////////////////////////////////////////////////////////////////
         // inline (enum-ish) values are not table-backed, so no source-table here. //
         /////////////////////////////////////////////////////////////////////////////
         return {results: filterInlinePossibleValues(searchTerm, possibleValues), sections: [], sourceTable: null};
      }
      else
      {
         const response = await qController.possibleValues(
            {
               tableName,
               processName,
               fieldNameOrPossibleValueSourceName: possibleValueSourceName ?? fieldName,
               searchTerm: searchTerm ?? "",
               values: otherValues,
               useCase,
               possibleValueSourceFilter: fieldPossibleValueProps.possibleValueSourceFilter,
               processUUID
            });
         return {results: response.results, sections: response.sections ?? [], sourceTable: response.sourceTable ?? null};
      }
   };


   /***************************************************************************
    ** turn record ids (from the add-new or search modal) into possible-values,
    ** by asking the PVS endpoint for them.  going back through the endpoint is
    ** what gets the PVS's own label formatting - and it re-applies the source's
    ** filter, so an id that doesn't satisfy it simply doesn't come back, which
    ** is how we detect a record created or picked outside this field's criteria.
    ***************************************************************************/
   const resolveIdsToPossibleValues = async (ids: any[]): Promise<QPossibleValue[]> =>
   {
      const response = await qController.possibleValues(
         {
            tableName,
            processName,
            fieldNameOrPossibleValueSourceName: possibleValueSourceName ?? fieldName,
            ids,
            values: otherValues,
            useCase,
            possibleValueSourceFilter: fieldPossibleValueProps.possibleValueSourceFilter,
            processUUID
         });
      return (response.results ?? []);
   };


   /***************************************************************************
    **
    ***************************************************************************/
   useEffect(() =>
   {
      if (firstRender)
      {
         // console.log("First render, so not searching...");
         setFirstRender(false);
         return;

         /*
         if(!initiallyOpen)
         {
            console.log("returning because not initially open?");
            return;
         }
         */
      }
      // console.log("Use effect for searchTerm - searching!");

      let active = true;

      setLoading(true);
      (async () =>
      {
         // console.log(`doing a search with ${searchTerm}`);
         const {results, sections: loadedSections, sourceTable: loadedSourceTable} = await loadResults();

         if (tableMetaData == null && tableName)
         {
            let tableMetaData: QTableMetaData = await qController.loadTableMetaData(tableName);
            setTableMetaData(tableMetaData);
         }

         setLoading(false);
         // console.log("Results:")
         // console.log(`${results}`);
         if (active)
         {
            setOptions([...results]);
            setSections(loadedSections);
            setSourceTable(loadedSourceTable);
         }
      })();

      return () =>
      {
         active = false;
      };
   }, [searchTerm]);


   /***************************************************************************
    ** re-run the possible-value search behind the option list.
    **
    ** Separate from the searchTerm effect because that one only fires when the
    ** term itself changes, so it cannot be used to say "the underlying data
    ** moved" - which it does when a record is created or picked through one of
    ** the source-table modals.
    ***************************************************************************/
   const reloadOptions = () =>
   {
      (async () =>
      {
         setLoading(true);
         setOptions([]);

         console.log("Refreshing possible values...");
         const {results, sections: loadedSections, sourceTable: loadedSourceTable} = await loadResults();

         setLoading(false);
         setOptions([...results]);
         setSections(loadedSections);
         setSourceTable(loadedSourceTable);
         setOtherValuesWhenResultsWereLoaded(JSON.stringify(Object.fromEntries(otherValues)));
      })();
   };


   /***************************************************************************
    ** todo - finish... call it in onOpen?
    ***************************************************************************/
   const reloadIfOtherValuesAreChanged = () =>
   {
      if (JSON.stringify(Object.fromEntries(otherValues)) != otherValuesWhenResultsWereLoaded)
      {
         reloadOptions();
      }
   };


   /***************************************************************************
    **
    ***************************************************************************/
   const inputChanged = (event: React.SyntheticEvent, value: string, reason: string) =>
   {
      // console.log(`input changed.  Reason: ${reason}, setting search term to ${value}`);
      if (reason !== "reset")
      {
         // console.log(` -> setting search term to ${value}`);
         setSearchTerm(value);
      }
   };


   /***************************************************************************
    **
    ***************************************************************************/
   const handleBlur = (x: any) =>
   {
      setSearchTerm(null);
   };


   /***************************************************************************
    **
    ***************************************************************************/
   const handleChanged = (event: React.SyntheticEvent, value: any | any[], reason: string, details?: string) =>
   {
      /////////////////////////////////////////////////////////////////
      // for multiple mode, keep the searchTerm - but else, clear it //
      /////////////////////////////////////////////////////////////////
      if(!isMultiple)
      {
         setSearchTerm(null);
      }

      if (isMultiple)
      {
         currentMultipleValues.current = value ?? [];
      }

      ///////////////////////////////////////////////////////////////////////////////////
      // any change is the user moving on from whatever the last modal apply reported  //
      // - leaving the message up would keep telling them about a problem they already //
      // fixed by picking something else                                               //
      ///////////////////////////////////////////////////////////////////////////////////
      setSourceTableMessage(null);

      if (onChange)
      {
         if (isMultiple)
         {
            onChange(value);
         }
         else
         {
            onChange(value ? new QPossibleValue(value) : null);
         }
      }
      else if (setFieldValueRef && fieldName)
      {
         setFieldValueRef(fieldName, value ? value.id : null);
      }
   };


   /***************************************************************************
    ** apply record ids chosen in the add-new or search modal as this select's
    ** value(s) - resolving them through the PVS endpoint first, so the label is
    ** the source's own formatted label, and so a record that doesn't satisfy the
    ** source's filter is reported instead of silently selected.
    ***************************************************************************/
   const applyRecordIds = async (ids: string[]) =>
   {
      setSourceTableMessage(null);

      /////////////////////////////////////////////////////////////////////////////////////
      // the picker is asked for cap+1 ids, so more than the cap means the selection was //
      // genuinely cut off - trim, and say so below rather than quietly handing back a   //
      // round number the user reads as "all of them".                                   //
      /////////////////////////////////////////////////////////////////////////////////////
      let cappedAt: number = null;
      if (ids.length > MULTI_SELECT_PICK_MAX_RECORDS)
      {
         ids = ids.slice(0, MULTI_SELECT_PICK_MAX_RECORDS);
         cappedAt = MULTI_SELECT_PICK_MAX_RECORDS;
      }

      let resolvedValues: QPossibleValue[];
      try
      {
         resolvedValues = await resolveIdsToPossibleValues(ids);
      }
      catch (e: any)
      {
         ///////////////////////////////////////////////////////////////////////////////////
         // qController.possibleValues rethrows every failure, and both call sites invoke //
         // this as a floating promise - so without this the modal closes, the dropdown   //
         // is unchanged, and the only trace is an unhandled rejection in the console.    //
         ///////////////////////////////////////////////////////////////////////////////////
         console.error("Error resolving picked record ids to possible values", e);
         setSourceTableMessage(`Could not apply your selection: ${e?.message ?? "details not available."}  Please try again.`);
         return;
      }

      if (resolvedValues.length == 0)
      {
         setSourceTableMessage(`That ${sourceTable?.label ?? "record"} does not meet this field's criteria, so it cannot be selected here.`);
         return;
      }

      ///////////////////////////////////////////////////////////////////////////////////
      // messages are computed here but not set until after handleChanged below, which //
      // clears the message as part of any value change                                //
      ///////////////////////////////////////////////////////////////////////////////////
      const messages: string[] = [];

      if (cappedAt != null)
      {
         messages.push(`Only the first ${cappedAt.toLocaleString()} matching records were added.`);
      }

      if (resolvedValues.length < ids.length)
      {
         const missingCount = ids.length - resolvedValues.length;
         messages.push(`${missingCount} of the ${ids.length} selected records do not meet this field's criteria and ${missingCount == 1 ? "was" : "were"} not added.`);
      }

      if (isMultiple)
      {
         const existingValues = currentMultipleValues.current ?? [];

         /////////////////////////////////////////////////////////////////////////////////
         // a record already selected must not come back as a second chip with the same //
         // id - which would also send the duplicate id on to onChange                  //
         /////////////////////////////////////////////////////////////////////////////////
         const existingIds     = new Set(existingValues.map((value: any) => String(value?.id)));
         const valuesToAdd     = resolvedValues.filter(value => !existingIds.has(String(value?.id)));
         const alreadySelected = resolvedValues.length - valuesToAdd.length;
         if (alreadySelected > 0)
         {
            messages.push(alreadySelected == 1 ? "1 selected record was already in this field." : `${alreadySelected} selected records were already in this field.`);
         }

         const newValues = [...existingValues, ...valuesToAdd];
         setValueFromModal(newValues);
         handleChanged(null, newValues, "selectOption");
      }
      else
      {
         setValueFromModal(resolvedValues[0]);
         handleChanged(null, resolvedValues[0], "selectOption");
      }

      if (messages.length > 0)
      {
         setSourceTableMessage(messages.join("  "));
      }

      /////////////////////////////////////////////////////////////////////////////////
      // and refresh the option list, so the record just created or picked has a row //
      // in it.  handleChanged only clears searchTerm for a single-select, which is  //
      // what makes that mode refresh as a side effect; multi-select had nothing     //
      // invalidating options, so re-opening the dropdown showed a list missing the  //
      // new chip - no checkbox to untick it with, and MUI's "none of the options    //
      // match" warning.                                                             //
      /////////////////////////////////////////////////////////////////////////////////
      reloadOptions();

      ////////////////////////////////////////////////////////////////////////////
      // remount the Autocomplete so it picks up the new defaultValue - see the //
      // note on valueFromModal for why this isn't done as a controlled value.  //
      ////////////////////////////////////////////////////////////////////////////
      setRemountKey(key => key + 1);
   };


   /***************************************************************************
    **
    ***************************************************************************/
   const filterOptions = (options: { id: any; label: string; }[], state: FilterOptionsState<{ id: any; label: string; }>): { id: any; label: string; }[] =>
   {
      /////////////////////////////////////////////////////////////////////////////////
      // this looks like a no-op, but it's important to have, otherwise, we can only //
      // get options whose text/label matches the input (e.g., not ids that match)   //
      /////////////////////////////////////////////////////////////////////////////////
      return (options);
   };


   /***************************************************************************
    **
    ***************************************************************************/
   // @ts-ignore
   const renderOption = (props: Object, option: any, {selected}) =>
   {
      let labelContent = (<>{option.label}</>);

      try
      {
         const field = tableMetaData?.fields.get(fieldName);
         if (field)
         {
            const adornment = field.getAdornment(AdornmentType.CHIP);
            if (adornment)
            {
               const color = adornment.getValue("color." + option.id) ?? "default";
               const iconName = adornment.getValue("icon." + option.id) ?? null;
               const iconElement = iconName ? <Icon>{iconName}</Icon> : null;
               labelContent = (<Chip label={option.label} color={color} icon={iconElement} size="small" variant="outlined" sx={{fontWeight: 500}} />);
            }
         }
      }
      catch (e)
      {
      }

      const descriptionLine = option.description
         ? (<Box component="span" sx={{display: "block", fontSize: "0.75rem", color: "text.secondary", mt: "2px", ...(isMultiple && {pl: "47px"})}}>
            {option.description}
         </Box>)
         : null;

      let content = (
         <Box sx={{display: "flex", flexDirection: "column"}}>
            <Box sx={{display: "flex", alignItems: "center"}}>
               {isMultiple && <Checkbox style={{marginRight: 8}} checked={selected} />}
               {labelContent}
            </Box>
            {descriptionLine}
         </Box>
      );

      ///////////////////////////////////////////////////////////////////////////////////////////////
      // we provide a custom renderOption method, to prevent a bug we saw during development,      //
      // where if multiple options had an identical label, then the widget would ... i don't know, //
      // show more options than it should - it was odd to see, and it could be fixed by changing   //
      // a PVS's format to include id - so the idea came, that maybe the LI's needed unique key    //
      // attributes.  so, doing this, w/ key=id, seemed to fix it.                                 //
      ///////////////////////////////////////////////////////////////////////////////////////////////
      return (
         <li {...props} key={option.id} style={{fontSize: "1rem", ...(anyOptionHasSection && {paddingLeft: "1.25rem"})}}>
            {content}
         </li>
      );
   };


   /***************************************************************************
    **
    ***************************************************************************/
   const bulkEditSwitchChanged = () =>
   {
      const newSwitchValue = !switchChecked;
      setSwitchChecked(newSwitchValue);
      setIsDisabled(!newSwitchValue);
      bulkEditSwitchChangeHandler(fieldName, newSwitchValue);
   };

   //////////////////////////////////////
   // compute section-grouping helpers //
   //////////////////////////////////////
   const anyOptionHasSection = options.some((o: any) => o.sectionId !== undefined && o.sectionId !== null);

   const sectionLabelById = useMemo(() =>
   {
      //////////////////////////////////////////////////////////////////
      // keys stringified to match groupBy's String(sectionId) lookup //
      //////////////////////////////////////////////////////////////////
      const m = new Map<string, string>();
      for (const s of sections)
      {
         m.set(String(s.id), s.label);
      }
      return m;
   }, [sections]);

   ////////////////////////////////////////////
   // for outlined style, adjust some styles //
   ////////////////////////////////////////////
   let autocompleteSX = {};
   if (variant == "outlined")
   {
      autocompleteSX = getAutocompleteOutlinedStyle(isDisabled);
   }

   const showAddNew             = sourceTable?.mayAddNew === true;
   const showSearch             = sourceTable?.maySearch === true;
   const showSourceTableFooter  = showAddNew || showSearch;

   /***************************************************************************
    ** Paper for the autocomplete popup, with a pinned footer carrying the
    ** source-table actions below the scrolling option list.  onMouseDown must be
    ** prevented on the footer, or the autocomplete blurs and unmounts this paper
    ** before the click handler ever runs.
    **
    ** Memoized because MUI renders it through AutocompletePaper's `as` prop: a
    ** fresh function identity is a different element TYPE, so React would unmount
    ** and remount the popup Paper - and the listbox DOM inside it - on every
    ** render while the popup is open, including every keystroke that sets
    ** searchTerm.
    ***************************************************************************/
   const PaperWithSourceTableFooter = useCallback((paperProps: any) =>
      (
         <Paper {...paperProps}>
            {paperProps.children}
            <Box
               className="dynamicSelectSourceTableFooter"
               onMouseDown={(e: React.MouseEvent) => e.preventDefault()}

               ///////////////////////////////////////////////////////////////////////////////
               // grayLines, not inputBorderColor - that one is for an input's outline, and //
               // this is a divider inside a menu.                                          //
               ///////////////////////////////////////////////////////////////////////////////
               ////////////////////////////////////////////////////////////////////////////////
               // rowGap 0, and not the columnGap: a button is already 32px tall against a   //
               // ~20px line box, so it carries its own vertical separation - adding a gap   //
               // on top of that put more space BETWEEN the two stacked actions than there   //
               // was above and below the pair, which read as them belonging to each other   //
               // less than to the divider and the Paper's edge.  The 4px here is what makes //
               // those three gaps come out even.                                            //
               ////////////////////////////////////////////////////////////////////////////////
               sx={{display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1, rowGap: 0, borderTop: `1px solid ${grayLines.main}`, px: 0.5, py: 0.5}}
            >
               {
                  showAddNew &&
                  <Button
                     data-qqq-id="pvsSourceTableAddNew"
                     startIcon={<Icon>add</Icon>}
                     sx={sourceTableActionButtonSX}
                     onClick={() =>
                     {
                        setOpen(false);
                        setAddNewModalIsOpen(true);
                     }}
                  >
                     Add new
                  </Button>
               }
               {
                  showSearch &&
                  <Button
                     data-qqq-id="pvsSourceTableSearch"
                     startIcon={<Icon>search</Icon>}
                     sx={sourceTableActionButtonSX}
                     onClick={() =>
                     {
                        setOpen(false);
                        setSearchModalIsOpen(true);
                     }}
                  >
                     Search
                  </Button>
               }
            </Box>
         </Paper>
      ), [showAddNew, showSearch]);

   const autocomplete = (
      <Box>
         <Autocomplete
            key={remountKey}
            id={overrideId ?? fieldName ?? possibleValueSourceName ?? "anonymous"}
            name={name}
            sx={autocompleteSX}
            open={open}
            openOnFocus={openOnFocus}
            fullWidth
            onOpen={() =>
            {
               reloadIfOtherValuesAreChanged();
               setOpen(true);
               // console.log("setting open...");
               if (options.length == 0)
               {
                  // console.log("no options yet, so setting search term to ''...");
                  setSearchTerm("");
               }
            }}
            onClose={() =>
            {
               setOpen(false);
            }}
            isOptionEqualToValue={(option, value) => value !== null && value !== undefined && option.id === value.id}
            getOptionLabel={(option) =>
            {
               if (option === null || option === undefined)
               {
                  return ("");
               }

               // @ts-ignore
               if (option && option.length)
               {
                  // @ts-ignore
                  option = option[0];
               }
               // @ts-ignore
               return option.label;
            }}
            options={options}
            loading={loading}
            inputValue={isMultiple ? searchTerm : undefined}
            onInputChange={inputChanged}
            onBlur={handleBlur}
            defaultValue={valueFromModal === undefined ? defaultValue : valueFromModal}
            {...(showSourceTableFooter && {PaperComponent: PaperWithSourceTableFooter})}
            // @ts-ignore
            onChange={handleChanged}
            noOptionsText={"No matches found"}
            onKeyPress={e =>
            {
               if (e.key === "Enter")
               {
                  e.preventDefault();
               }
            }}
            renderOption={renderOption}
            {...(anyOptionHasSection && {
               groupBy: (option: any) => String(option.sectionId ?? ""),
               renderGroup: (params: AutocompleteRenderGroupParams) =>
                  (
                     <li key={params.key}>
                        <Box sx={{pl: "4px", pr: 2, py: 1, fontSize: "1rem", fontWeight: 500, color: "text.secondary"}}>
                           {sectionLabelById.get(params.group) ?? params.group}
                        </Box>
                        <ul style={{padding: 0, margin: 0}}>{params.children}</ul>
                     </li>
                  ),
            })}
            filterOptions={filterOptions}
            disabled={isDisabled}
            multiple={isMultiple}
            disableCloseOnSelect={isMultiple}
            limitTags={limitTags}
            slotProps={{popper: {className: "DynamicSelectPopper"}}}
            renderInput={(params) => (
               <TextField
                  {...params}
                  label={fieldLabel}
                  variant={variant}
                  autoComplete="off"
                  type="search"
                  InputProps={{
                     ...params.InputProps,
                     endAdornment: (
                        <React.Fragment>
                           {loading ? <CircularProgress color="inherit" size={20} /> : null}
                           {params.InputProps.endAdornment}
                        </React.Fragment>
                     ),
                  }}
               />
            )}
         />
         {
            inForm &&
            <Box mt={0.75}>
               <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
                  {!isDisabled && <div className="fieldErrorMessage"><ErrorMessage name={overrideId ?? fieldName ?? possibleValueSourceName ?? "anonymous"} render={msg => <span data-field-error="true">{msg}</span>} /></div>}
               </MDTypography>
            </Box>
         }
      </Box>
   );

   const seedValues = buildSeedValuesFromFilter(sourceTable?.filter);

   const sourceTableExtras = (
      <>
         {
            sourceTableMessage &&
            <Box mt={0.75}>
               <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
                  {sourceTableMessage}
               </MDTypography>
            </Box>
         }
         {
            addNewModalIsOpen && sourceTable &&
            <RecordScreenModal
               open={true}
               tableName={sourceTable.name}
               defaultValues={seedValues}
               disabledFields={Object.keys(seedValues)}
               onSaved={(recordId: string) => applyRecordIds([recordId])}
               onClose={() => setAddNewModalIsOpen(false)}
            />
         }
         {
            searchModalIsOpen && sourceTable &&
            <RecordPickerModal
               tableName={sourceTable.name}
               heading={`Select ${sourceTable.label}`}
               saveButtonLabel="Select"
               saveButtonIcon="check"
               selectionMode={isMultiple ? "multiple" : "single"}
               initialQueryFilter={sourceTable.filter}
               lockedCriteriaCount={sourceTable.filter?.criteria?.length ?? 0}
               resolveToRecordIdsLimit={isMultiple ? MULTI_SELECT_PICK_MAX_RECORDS + 1 : 1}
               onSelect={(selection: string[] | QQueryFilter) =>
               {
                  setSearchModalIsOpen(false);

                  //////////////////////////////////////////////////////////////////////////////
                  // resolveToRecordIdsLimit is set, so the modal always hands back ids - the //
                  // guard is only here because the prop's type still admits a QQueryFilter.  //
                  //////////////////////////////////////////////////////////////////////////////
                  if (Array.isArray(selection))
                  {
                     applyRecordIds(selection);
                  }
               }}
               onClose={() => setSearchModalIsOpen(false)}
            />
         }
      </>
   );


   if (bulkEditMode)
   {
      return (
         <Box mb={1.5} display="flex" flexDirection="row">
            <Box alignItems="baseline" pt={1}>
               <Switch
                  id={`bulkEditSwitch-${fieldName}`}
                  checked={switchChecked}
                  onClick={bulkEditSwitchChanged}
                  sx={{
                     top: "-4px",
                     "& .MuiSwitch-track": {
                        height: 20,
                        borderRadius: 10,
                        top: -3,
                        position: "relative"
                     }
                  }}
               />
            </Box>
            <Box width="100%">
               {autocomplete}
               {sourceTableExtras}
            </Box>
         </Box>
      );
   }
   else
   {
      return (
         <Box className="DynamicSelectAutoCompleteWrapper">
            {autocomplete}
            {sourceTableExtras}
         </Box>
      );
   }


}

export default DynamicSelect;
